import React, {ReactElement, ReactNode, useContext, useEffect, useState} from "react";
import {Col, Grid, Row} from "@zendeskgarden/react-grid";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row as TableRow, Table} from "@zendeskgarden/react-tables";
import {Checkbox, Field, Label, MediaInput, Toggle} from "@zendeskgarden/react-forms";
import {
    JobType,
    useDeactivateAcquisitionPropertiesMutation,
    useListAcquisitionPropertiesLazyQuery,
    VersionType
} from "../../__generated__/generated_types";
import {Button} from "@zendeskgarden/react-buttons";
import {History, SearchRounded} from "@material-ui/icons";
import {AcquisitionPropertyCreateModal} from "./AcquisitionPropertyCreateModal";
import {AcquisitionPropertyEditModal} from "./AcquisitionPropertyEditModal";
import {
    AcquisitionPropertyAccountValuesModal
} from "./components/account-values-modal/AcquisitionPropertyAccountValuesModal";
import SelectionBar from "./components/SelectionBar";
import PropertyDeleteConfirmation from "./components/PropertyDeleteConfirmation";
import {toast} from "react-toastify";

import {ViziblyTheme} from "../analyst/ViziblyZDGTheme";

import * as css from "./styles/acquisitionProperties.module.scss";
import {AdminWrapper} from "../admin/AdminWrapper";
import {useProperties} from "../../contexts/properties/PropertiesContext";
import {asBudgetProperty} from "../../contexts/properties/logic";
import {AcquisitionProperty} from "./types";
import {userIsAdmin} from "../../authorization/AuthorizationCheckers";
import {AuthContext} from "../../contexts/AuthContext";
import {RunningJobRendererLatestJob} from "../admin/jobs/RunningJobRenderer";
import {Body as ModalBody, Close, Header, Modal} from "@zendeskgarden/react-modals";
import {ListJobs, ListJobsDisplayColumn} from "../admin/jobs/ListJobs";
import {ReactComponent as CheckCircleFill} from "@zendeskgarden/svg-icons/src/16/check-circle-fill.svg";
import {ReactComponent as AlertWarningFill} from "@zendeskgarden/svg-icons/src/16/alert-warning-fill.svg";
import {Tooltip} from "@zendeskgarden/react-tooltips";
import {AcquisitionPropertyRentRollModal} from "./AcquisitionPropertyRentRollModal";
import {useFetchUpdatesForJobs} from "./logic/useFetchUpdatesForJobs";
import {CopyToNormalModal} from "./components/copy-to-normal/CopyToNormalModal";
import {getRevenueModelDisplayName} from "../../constants/Strings";
import {CopyDriverCrossCoaModalFixedDestination} from "../property-drivers/components/CopyDriverCrossCoaModal";

export enum AcquisitionPropertyVersion {
    Year1 = "Year 1",
    T12 = "T12",
}

function isTruthy(val: any): boolean {
    return !!val;
}

export default function AcquisitionProperties(): ReactElement {
    const {user} = useContext(AuthContext);

    const [includeDeactivated, setIncludeDeactivated] = useState<boolean>(false);
    const [acquisitionProperties, setAcquisitionProperties] = useState<AcquisitionProperty[]>([]);
    const [search, setSearch] = useState<string | undefined>(undefined);
    const [filteredProperties, setFilteredProperties] = useState<AcquisitionProperty[]>([]);
    const [selectedProperty, setSelectedProperty] = useState<AcquisitionProperty | undefined>(undefined);
    const [createFormVisible, setCreateFormVisible] = useState<boolean>(false);
    const [editFormVisible, setEditFormVisible] = useState<boolean>(false);
    const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState<boolean>(false);
    const [accountValuesUploadVisible, setAccountValuesUploadVisible] = useState<boolean>(false);
    const [rentRollUploadVisible, setRentRollUploadVisible] = useState<boolean>(false);
    const [currentJobId, setCurrentJobId] = useState<string | undefined>(undefined);
    const [showJobLog, setShowJobLog] = useState<boolean>(false);
    const [copyToNormalVisible, setCopyToNormalVisible] = useState<boolean>(false);
    const [copyFromNormalVisible, setCopyFromNormalVisible] = useState<boolean>(false);

    const [getAcquisitionProperties, {data, loading}] = useListAcquisitionPropertiesLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [deactivateProperties, {loading: loadingDeactivate}] = useDeactivateAcquisitionPropertiesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const {activateAcquisitionProperty, currentProperty, allNormalProperties} = useProperties();

    const {addForUpdate, updates} = useFetchUpdatesForJobs();

    function selectAll(): void {
        setSelectedProperties(acquisitionProperties.map(x => x.id));
    }

    function deselectAll(): void {
        setSelectedProperties([]);
    }

    function selectProperty(propertyId: string): void {
        setSelectedProperties([...selectedProperties, propertyId]);
    }

    function deselectProperty(propertyId: string): void {
        const newSelection = [...selectedProperties];
        newSelection.splice(newSelection.indexOf(propertyId), 1);
        setSelectedProperties(newSelection);
    }

    function deactivateSelectedProperties(): void {
        deactivateProperties({
            variables: {
                propertyIds: selectedProperties,
            }
        })
            .then(() => {
                setConfirmDeleteVisible(false);
                deselectAll();
                toast.success('Properties deactivated successfully');
                getAcquisitionProperties({
                    variables: {
                        includeDeactivated: includeDeactivated
                    }
                });
            })
            .catch(() => { /* Do Nothing */ });
    }

    function doActivateAcquisitionProperty(property: AcquisitionProperty): void {
        const budgetProperty = asBudgetProperty(property);
        const all = acquisitionProperties.map(p => asBudgetProperty(p));
        const similarProperties = all
                .filter(p => p.id !== property.id && p.reforecastYear === property.reforecastYear);
        activateAcquisitionProperty(budgetProperty, similarProperties, all);
    }

    useEffect(() => {
        getAcquisitionProperties({
            variables: {
                includeDeactivated: false
            }
        });

        const handleEsc = (evt: KeyboardEvent) => {
            if (evt.key === 'Escape') {
                deselectAll();
            }
        };

        window.addEventListener('keydown', handleEsc);

        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, []);

    useEffect(() => {
        getAcquisitionProperties({
            variables: {
                includeDeactivated: includeDeactivated
            }
        });
    }, [includeDeactivated]);

    useEffect(() => {
        if(!loading && data) {
            setAcquisitionProperties(data.listAcquisitionProperties);
        }
    }, [data, loading]);

    useEffect(() => {
        // We listen on data and loading because we are updating
        // the acquisitionProperties state. We don't want an infinite
        // loop by listening on that.
        if(!data || loading || !updates || updates.size === 0) {
            return;
        }

        const updated: AcquisitionProperty[] = [];
        for(const val of data.listAcquisitionProperties) {
            const update = updates.get(val.id);
            if(update) {
                updated.push(update);
            } else {
                updated.push(val);
            }
        }

        setAcquisitionProperties(updated);
    }, [data, loading, updates]);

    useEffect(() => {
        if (!search) {
            setFilteredProperties(acquisitionProperties);
            return;
        }

        const filtered: AcquisitionProperty[] = [];
        const searchLower = search.toLowerCase();
        for (const property of acquisitionProperties) {
            if (property.name.toLowerCase().includes(searchLower)) {
                filtered.push(property);
            }
        }

        setFilteredProperties(filtered);
    }, [search, acquisitionProperties]);

    const renderValid = (description: string, val: any, details?: ReactNode | null): ReactElement => {
        let state = <CheckCircleFill aria-label={"Complete Checkmark"} />;
        if(!isTruthy(val)) {
            state = <AlertWarningFill aria-label={"Incomplete Warning"} />;
        }

        let stateDescription = <>{state} {description}</>;
        if(details) {
           stateDescription = <Tooltip content={details}>{stateDescription}</Tooltip>;
        }
        return <span>{stateDescription}</span>;
    };

    return (
        <AdminWrapper>
            <ThemeProvider theme={ViziblyTheme}>
                <Grid className={css.sectionWrapper}>
                    <Row>
                        <Col>
                            <h3 className={css.pageTitle}>Budget Builder</h3>
                        </Col>
                        <Col className={css.headerActionsWrapper}>
                            <RunningJobRendererLatestJob jobType={JobType.AcquisitionPropertyParseFiles} jobId={currentJobId} />
                            <Button size="small" onClick={() => setShowJobLog(true)}>
                                <Button.StartIcon><History /></Button.StartIcon>
                                View Job History
                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className={css.tableHeaderActions}>
                                <div className={css.tableHeaderActionsRight}>
                                    <Field>
                                        <MediaInput
                                            className={css.tableHeaderActionsSearch}
                                            placeholder="Search"
                                            isCompact
                                            onChange={evt => setSearch(evt.target.value.toLowerCase())}
                                            start={
                                                <SearchRounded />
                                            }
                                        />
                                    </Field>
                                    <Field>
                                        <Toggle
                                                checked={includeDeactivated}
                                                onChange={(e) => setIncludeDeactivated(e.target.checked)}
                                        >
                                            <Label>Include Deactivated</Label>
                                        </Toggle>
                                    </Field>
                                    {
                                        userIsAdmin(user) &&
                                        <Button isPrimary className={css.tableHeaderActionsButton} onClick={() => setCreateFormVisible(true)}>Add Property</Button>
                                    }
                                </div>
                            </div>
                            <Table>
                                <Head>
                                    <HeaderRow>
                                        {
                                            userIsAdmin(user) &&
                                                <HeaderCell isMinimum>
                                                    <Field>
                                                        <Checkbox
                                                            checked={!loading && selectedProperties.length === filteredProperties.length && filteredProperties.length > 0}
                                                            onChange={() => {
                                                                filteredProperties.length === selectedProperties.length ? deselectAll() : selectAll();
                                                            }}
                                                            indeterminate={selectedProperties.length !== 0 && selectedProperties.length !== filteredProperties.length}
                                                        >
                                                            <Label style={{position: "revert"}}/>
                                                        </Checkbox>
                                                    </Field>
                                                </HeaderCell>
                                        }
                                        <HeaderCell>Name</HeaderCell>
                                        <HeaderCell>Budget Year</HeaderCell>
                                        <HeaderCell>Budget Start Month</HeaderCell>
                                        <HeaderCell>Revenue Model</HeaderCell>
                                        <HeaderCell>Status</HeaderCell>
                                        <HeaderCell />
                                    </HeaderRow>
                                </Head>
                                <Body>
                                    {loading ? (
                                            <TableRow>
                                                <Cell colSpan={8} style={{textAlign: "center"}}>Loading...</Cell>
                                            </TableRow>
                                    ) : filteredProperties.length === 0 ? (
                                            <TableRow>
                                                <Cell colSpan={8} style={{textAlign: "center"}}>No acquisition properties found</Cell>
                                            </TableRow>
                                    ) : (
                                            filteredProperties.map((property) => (
                                                    <TableRow key={property.id} style={{verticalAlign: "middle"}}>
                                                        {
                                                            userIsAdmin(user) &&
                                                                <Cell isMinimum>
                                                                    <Field className={css.checkboxCell}>
                                                                        <Checkbox
                                                                                checked={selectedProperties.includes(property.id)}
                                                                                onChange={() => {
                                                                                    selectedProperties.includes(property.id) ? deselectProperty(property.id) : selectProperty(property.id);
                                                                                }}
                                                                        >
                                                                            <Label />
                                                                        </Checkbox>
                                                                    </Field>
                                                                </Cell>
                                                        }
                                                        <Cell>{property.name}</Cell>
                                                        <Cell>{property.acquisitionBudgetYear}</Cell>
                                                        <Cell>{getMonthName(property.acquisitionBudgetStartMonth)}</Cell>
                                                        <Cell>{getRevenueModelDisplayName(property.revenueConfiguration)}</Cell>
                                                        <Cell>
                                                            <ul style={{listStyle: "none", marginLeft: 0, paddingLeft: 0}}>
                                                                <li>{renderValid(property.isDeactivated ? 'Deactivated' : 'Active', !property.isDeactivated)}</li>
                                                                {property.state.usePropertyCoa ?
                                                                    <>
                                                                        <li>{renderValid("Use Property CoA", property.state.usePropertyCoa)}</li>
                                                                    </> :
                                                                    <>
                                                                        <li>{renderValid("Has External CoA", property.state.externalCoaId)}</li>
                                                                        <li>{renderValid("External CoA Mapping", property.state.externalCoaHasMapping)}</li>
                                                                    </>
                                                                }
                                                                <li>{renderValid("Account Values Processed", property.state.accountValuesProcessedTime, property.state.accountValuesProcessedTime)}</li>
                                                                <li>{renderValid("Rent Roll Processed", property.state.rentRollProcessedTime, property.state.rentRollProcessedTime)}</li>
                                                            </ul>
                                                        </Cell>
                                                        <Cell>
                                                            {currentProperty?.id === property.id ?
                                                                "Enabled for Budgeting" :
                                                                <Button
                                                                    onClick={() => doActivateAcquisitionProperty(property)}
                                                                    size="small"
                                                                >
                                                                    Enable Budgeting
                                                                </Button>
                                                            }
                                                        </Cell>
                                                    </TableRow>
                                            ))
                                    )}
                                </Body>
                            </Table>
                        </Col>
                    </Row>
                </Grid>
                {userIsAdmin(user) &&
                    <>
                        <AcquisitionPropertyCreateModal
                            formVisible={createFormVisible}
                            onModalClose={() => setCreateFormVisible(false)}
                            onCreate={() => getAcquisitionProperties({
                                variables: {
                                    includeDeactivated: includeDeactivated
                                }
                            })}
                        />

                        {editFormVisible && selectedProperty &&
                            <AcquisitionPropertyEditModal
                                property={selectedProperty}
                                formVisible={true}
                                onModalClose={() => {
                                    setSelectedProperty(undefined);
                                    setEditFormVisible(false);
                                }}
                                onUpdate={() => getAcquisitionProperties({
                                    variables: {
                                        includeDeactivated: includeDeactivated
                                    }
                                })}
                            />
                        }

                        <SelectionBar
                            user={user}
                            selected={selectedProperties}
                            onEdit={() => {
                                if(selectedProperties.length === 1) {
                                    const property = acquisitionProperties.find(x => x.id === selectedProperties[0]);
                                    if(property) {
                                        setSelectedProperty(property);
                                        setEditFormVisible(true);
                                    }
                                }
                            }}
                            onDelete={() => setConfirmDeleteVisible(true)}
                            onUploadAccountValues={() => {
                                if(selectedProperties.length === 1) {
                                    setAccountValuesUploadVisible(true);
                                }
                            }}
                            onUploadRentRoll={(jobId, propertyId) => {
                                setCurrentJobId(jobId);
                                addForUpdate(jobId, propertyId);
                            }}
                            onCopyToNormal={() => {
                                if(selectedProperties.length === 1) {
                                    const property = acquisitionProperties.find(x => x.id === selectedProperties[0]);
                                    if(property) {
                                        setSelectedProperty(property);
                                        setCopyToNormalVisible(true);
                                    }
                                }
                            }}
                            onCopyFromNormal={() => {
                                if(selectedProperties.length === 1) {
                                    const property = acquisitionProperties.find(x => x.id === selectedProperties[0]);
                                    if(property) {
                                        setSelectedProperty(property);
                                        setCopyFromNormalVisible(true);
                                    }
                                }
                            }}
                            name="Property"
                            namePlural="Properties"
                            enableBulk={false}
                        />

                        {confirmDeleteVisible && (
                            <PropertyDeleteConfirmation
                                properties={acquisitionProperties}
                                selectedProperties={selectedProperties}
                                loading={loadingDeactivate}
                                onClose={() => setConfirmDeleteVisible(false)}
                                onConfirm={deactivateSelectedProperties}
                            />
                        )}

                        {accountValuesUploadVisible && selectedProperties.length === 1 && selectedProperties[0] && (
                            <AcquisitionPropertyAccountValuesModal
                                propertyId={selectedProperties[0]}
                                jobCreated={(jobId, propertyId) => {
                                    setCurrentJobId(jobId);
                                    addForUpdate(jobId, propertyId);
                                }}
                                onModalClose={() => setAccountValuesUploadVisible(false)}
                            />
                        )}

                        {rentRollUploadVisible && selectedProperties.length === 1 && selectedProperties[0] && (
                                <AcquisitionPropertyRentRollModal
                                        propertyId={selectedProperties[0]}
                                        jobCreated={(jobId, propertyId) =>  {
                                            setCurrentJobId(jobId);
                                            addForUpdate(jobId, propertyId);
                                        }}
                                        onModalClose={() => setRentRollUploadVisible(false)}
                                />
                        )}

                        {copyToNormalVisible && selectedProperties.length === 1 && selectedProperty && (
                            <CopyToNormalModal
                                acquisitionProperty={selectedProperty}
                                onModalClose={() => setCopyToNormalVisible(false)}
                            />
                        )}
                        {copyFromNormalVisible && selectedProperties.length === 1 && selectedProperty && (
                            <CopyDriverCrossCoaModalFixedDestination
                                destination={{
                                    property: asBudgetProperty(selectedProperty),
                                    budgetYear: selectedProperty.reforecastYear + 1,
                                    version: VersionType.Reforecast
                                }}
                                allProperties={allNormalProperties || []}
                                onModalClose={() => setCopyFromNormalVisible(false)}
                                title="Import Modeling Methods"
                            />
                        )}
                    </>
                }
                {showJobLog &&
                    <Modal
                            isLarge
                            onClose={() => setShowJobLog(false)}
                    >
                        <Header>
                            Acquisition Property Upload Jobs
                        </Header>
                        <ModalBody>
                            <ListJobs
                                    jobType={JobType.AcquisitionPropertyParseFiles}
                                    visibleColumns={[
                                        ListJobsDisplayColumn.user,
                                        ListJobsDisplayColumn.status,
                                        ListJobsDisplayColumn.created,
                                        ListJobsDisplayColumn.started,
                                        ListJobsDisplayColumn.ended,
                                        ListJobsDisplayColumn.errorMessage,
                                        ListJobsDisplayColumn.details,
                                    ]}
                            />
                        </ModalBody>
                        <Close aria-label="Close modal" />
                    </Modal>
                }
            </ThemeProvider>
        </AdminWrapper>
    );
}

function getMonthName(monthIndex: number): string {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex] || '';
}
